import { useState } from 'react';
import { DateInput } from '../shared/DateInput';
import { useDateTime } from '../../hooks/useDateTime';
import { localIsoDate } from '../../utils/local-date';
import { useQuery } from '@tanstack/react-query';
import { Badge, Button, Select, Spinner, Text, makeStyles, tokens } from '@fluentui/react-components';
import { useT } from '../../i18n';
import { attendanceErrorMessage } from '../../utils/attendance-error';
import { fetchLotes } from '../../api/lotes.api';
import { fetchAttendanceReport, fetchAttendanceReportXlsx, fetchCrews, type EmployeeStatus } from '../../api/attendance.api';

const useStyles = makeStyles({
  row: { display: 'flex', gap: tokens.spacingHorizontalS, flexWrap: 'wrap', alignItems: 'center' },
  stats: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: tokens.spacingHorizontalM },
  stat: { padding: tokens.spacingVerticalM, border: `1px solid ${tokens.colorNeutralStroke2}`, borderRadius: tokens.borderRadiusMedium },
  muted: { color: tokens.colorNeutralForeground3 },
  danger: { color: tokens.colorPaletteRedForeground1 },
  wrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', minWidth: '640px' },
  th: { textAlign: 'left', padding: tokens.spacingVerticalXS, borderBottom: `1px solid ${tokens.colorNeutralStroke2}`, color: tokens.colorNeutralForeground3, fontWeight: 600, fontSize: tokens.fontSizeBase200 },
  td: { padding: tokens.spacingVerticalS, borderBottom: `1px solid ${tokens.colorNeutralStroke3}` },
  num: { textAlign: 'right', fontVariantNumeric: 'tabular-nums' },
});

const iso = (d: Date) => localIsoDate(d);
const STATUS_COLOR: Record<EmployeeStatus, 'success' | 'warning' | 'danger'> = { ACTIVE: 'success', PRE_TERMINATION: 'warning', INACTIVE: 'danger' };

/** Reporte de asistencia por periodo: por persona y por día, con descarga a Excel. */
export function AttendanceReportPanel({ tenantId }: { tenantId: string }) {
  const t = useT();
  const a = t.attendancePage;
  const s = useStyles();
  const fmt = useDateTime();
  const now = new Date();
  const [from, setFrom] = useState(iso(new Date(now.getFullYear(), now.getMonth(), 1)));
  const [to, setTo] = useState(iso(now));
  const [loteId, setLoteId] = useState('');
  const [crewId, setCrewId] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const lotes = useQuery({ queryKey: ['lotes-for-report'], queryFn: () => fetchLotes(null), staleTime: 5 * 60_000 });
  const crews = useQuery({ queryKey: ['att-crews', tenantId], queryFn: () => fetchCrews(tenantId), staleTime: 5 * 60_000 });
  const filters = { from, to, loteId: loteId || undefined, crewId: crewId || undefined };
  const report = useQuery({ queryKey: ['att-report', tenantId, from, to, loteId, crewId], queryFn: () => fetchAttendanceReport(tenantId, filters), enabled: !!from && !!to, retry: false });

  const rangeError = (report.error as { response?: { data?: { error?: string } } } | null)?.response?.data?.error;
  const download = async () => {
    setBusy(true); setError('');
    try {
      const { filename, blob } = await fetchAttendanceReportXlsx(tenantId, filters);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = filename; link.click();
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (e) { setError(attendanceErrorMessage(e, a.errors, a.genericError)); } finally { setBusy(false); }
  };

  const data = report.data;
  const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);
  const loteName = (id: string) => lotes.data?.find((l) => l.loteId === id)?.name ?? id;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM }}>
      <div className={s.row}>
        <DateInput aria-label={a.from} value={from} onValueChange={setFrom} />
        <DateInput aria-label={a.to} value={to} onValueChange={setTo} />
        <Select aria-label={a.lote} value={loteId} onChange={(_, d) => setLoteId(d.value)}>
          <option value="">{a.allLotes}</option>
          {lotes.data?.map((l) => <option key={l.loteId} value={l.loteId}>{l.name}</option>)}
        </Select>
        <Select aria-label={a.colCrew} value={crewId} onChange={(_, d) => setCrewId(d.value)}>
          <option value="">{a.allCrews}</option>
          {crews.data?.map((c) => <option key={c.crewId} value={c.crewId}>{c.name}</option>)}
        </Select>
        <Button appearance="primary" disabled={busy || !data} onClick={download}>{a.download}</Button>
      </div>
      <Text className={s.muted}>{a.reportHint}</Text>
      {error && <Text className={s.danger} role="alert">{error}</Text>}
      {(rangeError === 'INVALID_RANGE' || rangeError === 'RANGE_TOO_LARGE') && <Text className={s.danger} role="alert">{a.rangeError}</Text>}
      {report.isLoading && <Spinner size="small" label={a.loading} />}

      {data && data.days.length === 0 && <Text className={s.muted}>{a.reportEmpty}</Text>}
      {data && data.days.length > 0 && (
        <>
          <div className={s.stats}>
            {[
              [a.totalSessions, String(data.totals.sessions)], [a.colPresent, String(data.totals.present)],
              [a.colAbsent, String(data.totals.absent)], [a.colJustified, String(data.totals.justified)],
              [a.extrasTotal, String(data.totals.extras)], [a.colPct, pct(data.totals.pct)],
            ].map(([label, value]) => (
              <div key={label} className={s.stat}><Text className={s.muted} block size={200}>{label}</Text><Text size={600} weight="semibold">{value}</Text></div>
            ))}
          </div>

          <Text weight="semibold">{a.byPerson}</Text>
          <div className={s.wrap}>
            <table className={s.table}>
              <thead><tr>
                <th className={s.th}>{a.colName}</th><th className={s.th}>{a.colStatus}</th>
                <th className={`${s.th} ${s.num}`}>{a.colPresent}</th><th className={`${s.th} ${s.num}`}>{a.colAbsent}</th>
                <th className={`${s.th} ${s.num}`}>{a.colJustified}</th><th className={`${s.th} ${s.num}`}>{a.colUndecided}</th>
                <th className={`${s.th} ${s.num}`}>{a.colPct}</th>
                <th className={`${s.th} ${s.num}`}>{a.colLate}</th><th className={`${s.th} ${s.num}`}>{a.colHours}</th>
              </tr></thead>
              <tbody>
                {data.persons.map((p) => (
                  <tr key={p.employeeId}>
                    <td className={s.td}>{p.name}</td>
                    <td className={s.td}><Badge appearance="tint" color={STATUS_COLOR[p.status]}>{a[`status${p.status}` as const]}</Badge></td>
                    <td className={`${s.td} ${s.num}`}>{p.present}</td><td className={`${s.td} ${s.num}`}>{p.absent}</td>
                    <td className={`${s.td} ${s.num}`}>{p.justified}</td><td className={`${s.td} ${s.num}`}>{p.undecided}</td>
                    <td className={`${s.td} ${s.num}`}>{pct(p.pct)}</td>
                    <td className={`${s.td} ${s.num}`}>{p.late}</td>
                    <td className={`${s.td} ${s.num}`}>{(p.workedMinutes / 60).toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <Text weight="semibold">{a.byDay}</Text>
          <div className={s.wrap}>
            <table className={s.table}>
              <thead><tr>
                <th className={s.th}>{a.colDate}</th><th className={s.th}>{a.colLote}</th>
                <th className={`${s.th} ${s.num}`}>{a.colExpected}</th><th className={`${s.th} ${s.num}`}>{a.colPresent}</th>
                <th className={`${s.th} ${s.num}`}>{a.colAbsent}</th><th className={`${s.th} ${s.num}`}>{a.colJustified}</th>
                <th className={`${s.th} ${s.num}`}>{a.colExtras}</th>
              </tr></thead>
              <tbody>
                {data.days.map((d) => (
                  <tr key={d.sessionId}>
                    <td className={s.td}>{fmt.date(d.date)}</td><td className={s.td}>{loteName(d.loteId)}</td>
                    <td className={`${s.td} ${s.num}`}>{d.expected}</td><td className={`${s.td} ${s.num}`}>{d.present}</td>
                    <td className={`${s.td} ${s.num}`}>{d.absent}</td><td className={`${s.td} ${s.num}`}>{d.justified}</td>
                    <td className={`${s.td} ${s.num}`}>{d.extras}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
